import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";

import { swmsSelector, setCurrentSWMS } from "reduxStore/swms";
import { useAppDispatch } from "reduxStore/store";
import swms, { SWMSResponse } from "services/swms";
import { SwmsDto } from "services/users";

export type SwmsWizardRouteState = {
  jobId?: number | string;
  worksiteId?: number;
  projectId?: number;
  swmsId?: number;
  swmsPerson?: SWMSResponse;
  [key: string]: any;
};

type UseSwmsWizardStateOptions = {
  refreshOnMount?: boolean;
};

export const buildSwmsWizardState = <T extends SwmsWizardRouteState>(
  state: T,
  nextSwms?: SwmsDto | SWMSResponse | null,
  overrides: Partial<T> = {}
) => ({
  ...state,
  ...overrides,
  swmsId: nextSwms?.id ?? state?.swmsId ?? state?.swmsPerson?.id,
  swmsPerson: nextSwms ?? state?.swmsPerson,
});

export const useSwmsWizardState = <T extends SwmsWizardRouteState>(
  state: T | undefined,
  options: UseSwmsWizardStateOptions = {}
) => {
  const dispatch = useAppDispatch();
  const { currentSWMS } = useSelector(swmsSelector);
  const [isSyncingCurrentSWMS, setIsSyncingCurrentSWMS] = useState(false);

  const swmsId = useMemo(
    () => state?.swmsId ?? state?.swmsPerson?.id ?? currentSWMS?.id,
    [currentSWMS?.id, state]
  );

  useEffect(() => {
    if (
      state?.swmsPerson?.id &&
      currentSWMS?.id !== state.swmsPerson.id
    ) {
      dispatch(setCurrentSWMS(state.swmsPerson as unknown as SwmsDto));
    }
  }, [currentSWMS?.id, dispatch, state]);

  useEffect(() => {
    if (!swmsId) {
      return;
    }

    if (!options.refreshOnMount && currentSWMS?.id === swmsId) {
      return;
    }

    let isMounted = true;

    const loadCurrentSWMS = async () => {
      setIsSyncingCurrentSWMS(true);
      try {
        const response = await swms.swmsDetails(swmsId);
        if (isMounted) {
          dispatch(setCurrentSWMS(response));
        }
      } catch (error) {
        console.log(error);
      } finally {
        if (isMounted) {
          setIsSyncingCurrentSWMS(false);
        }
      }
    };

    loadCurrentSWMS();

    return () => {
      isMounted = false;
    };
  }, [currentSWMS?.id, dispatch, options.refreshOnMount, swmsId]);

  return {
    swmsId,
    currentSWMS:
      currentSWMS?.id === swmsId
        ? currentSWMS
        : ((state?.swmsPerson as SwmsDto | SWMSResponse | undefined) ?? null),
    isSyncingCurrentSWMS,
    setCurrentSWMS: (nextSwms: SwmsDto | SWMSResponse | null) =>
      dispatch(setCurrentSWMS(nextSwms)),
    buildNextWizardState: (nextSwms?: SwmsDto | SWMSResponse | null, overrides = {}) =>
      buildSwmsWizardState(state ?? ({} as T), nextSwms, overrides),
  };
};
